//This feature is used to manage jobsites. It allows users to retrieve and toggle the status of jobsites. 
//It is for users of type business.

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import jobsitesService from "./jobsitesService";

const initialState = {
    jobsites: [], // Initialize as an empty array
    isError: false,
    isSuccess: false,
    isLoading: false,
    message: null,
}

//get  all of a  User's Sites
export const getJobsites = createAsyncThunk('jobsites/getAll', 
    async (_, thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token;
            const response = await jobsitesService.getJobsites(token);
         
            return response;
        } catch (error) {
            const message = 
                (error.response && error.response.data && error.response.data.message) ||
                error.message ||
                error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

//toggle the status of a jobsite, from active to inactive or vice versa
export const toggleJobsiteStatus = createAsyncThunk('jobsites/toggleStatus', 
    async( siteId, thunkAPI) =>{
        
        try {
            const token = thunkAPI.getState().auth.user.token
            return await jobsitesService.toggleJobsiteStatus( siteId,token)
        } catch (error) {
            const message = 
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString()
            
            toast.info(error.response.data.error); // Show error message using react-toastify
            // console.log(error)
        return thunkAPI.rejectWithValue(message)
        }
})

// soft delete a jobsite, this will toggle the status of the jobsite to inactive or active. 
// it also sets the isDeleted property to true. 
//This can not be undone by the user, so it is important to confirm the action before proceeding. 
export const deleteJobsite = createAsyncThunk('jobsites/delete', 
    async( jobsiteID, thunkAPI) =>{
        
        try {
            const token = thunkAPI.getState().auth.user.token
            return await jobsitesService.deleteJobsite( jobsiteID,token)
        } catch (error) {
            const message = 
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString()
            
            toast.info(error.response.data.error); // Show error message using react-toastify
            // console.log(error)
        return thunkAPI.rejectWithValue(message)
        }
})

export const assignForeman = createAsyncThunk('jobsites/foreman/assign', 
    async( foremanAssignData , thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            //foremanAssignData must contain the jobsiteId and the workerId of the foreman to be assigned.
            return await jobsitesService.assignForeman(foremanAssignData, token)
        } catch (error) {
            const message = 
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString()

            toast.info(error.response.data.error); // Show error message using react-toastify
            // console.log(error)
        return thunkAPI.rejectWithValue(message)
        }
})

// removeForeman
export const removeForeman = createAsyncThunk('jobsites/foreman/remove', 
    async( removeForemanData , thunkAPI) => {
        try {
            const token = thunkAPI.getState().auth.user.token
            //removeForemanData must contain the jobsiteId and the workerId of the foreman to be removed.
            return await jobsitesService.removeForeman(removeForemanData, token)
        } catch (error) {
            const message = 
            (error.response && error.response.data && error.response.data.message) ||
            error.message ||
            error.toString()

            toast.info(error.response.data.error); // Show error message using react-toastify
            // console.log(error)
        return thunkAPI.rejectWithValue(message)
        }
})

export const jobsitesSlice = createSlice({
    name:'jobsites',
    initialState,
    reducers:{
        reset: (state) => initialState,
        
      
    },
    extraReducers: (builder) =>{
        builder 
        .addCase(getJobsites.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(getJobsites.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            state.jobsites = action.payload;
        })
        .addCase(getJobsites.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
        })
        .addCase(toggleJobsiteStatus.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(toggleJobsiteStatus.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            //Update the incoming jobsite's active status in the state
            const { jobsiteId, activeStatus } = action.payload;
            state.jobsites = state.jobsites.map( js =>
                js.id === jobsiteId ? { ...js, isActive: activeStatus } : js
            );
        })
        .addCase(toggleJobsiteStatus.rejected, (state, action) => {
            state.isLoading = false;
            state.isSuccess = false;
            state.message = action.payload;
        })
        .addCase(deleteJobsite.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(deleteJobsite.fulfilled, (state, action) => {
            //TODO: when the jobsite is delete, fetch the jobsites again to update the state. 
            //Do this by using a useEffect in the component that displays the jobsites, the useEffect should track the state.message
            //and when it changes, it should fetch the jobsites again. 
            state.isLoading = false;
            state.isSuccess = true;
            state.message = action.payload.message || 'Jobsite deleted successfully';
        })
        .addCase(deleteJobsite.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload;
            toast.error(`Failed to delete jobsite: ${action.payload}`);
        })

        // Assign foreman to a jobsite
        .addCase(assignForeman.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(assignForeman.fulfilled, (state, action) => {
            
            state.isLoading = false;
            state.isSuccess = true;
            state.message = action.payload.message;
        })
        .addCase(assignForeman.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload.message;
        })

        // Remove foreman from a jobsite
        .addCase(removeForeman.pending, (state) => {
            state.isLoading = true;
        })
        .addCase(removeForeman.fulfilled, (state, action) => {
            state.isLoading = false;
            state.isSuccess = true;
            state.message = action.payload.message;
        })
        .addCase(removeForeman.rejected, (state, action) => {
            state.isLoading = false;
            state.isError = true;
            state.message = action.payload.message;
        })
        
    ;
}})
export const { reset } = jobsitesSlice.actions;
export default jobsitesSlice.reducer